import React from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import CompanySearchCombo from 'components/CreateComps/CompanySearchCombo';
import FormControl from 'components/FormControl';
import CurrencyInput from 'components/Inputs/CurrencyInput';
import ChoiceInput from 'components/Inputs/ChoiceInput';
import NetEffectiveRentField from 'components/CreateComps/FormSection/LeaseForm/LeaseTerms/NetEffectiveRentField';
import FreeRentMonths from 'components/CreateComps/FormSection/FormFields/FreeRentMonths';
import UnitOfMeasurementLegend from 'components/UnitOfMeasurementLegend';
import {
  I18N_AVANT_PLATFORM_LEASE_LABEL_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
  I18N_PLATFORM_ROLES_COMPANY_ACTION_PATH,
  I18N_PLATFORM_ROLES_COMPANY_LABEL_PATH,
  I18N_PLATFORM_ROLES_COMPANY_PROMPT_PATH,
} from 'constants/i18n';
import { YES_NO_OPTIONS } from 'constants/commonOptions';
import { UNITS_MEASUREMENT_AREA } from 'constants/unitOfMeasurement';
import { SIZE_GROWTH_OPTIONS } from 'constants/expandingContracting';
import { BrokerCompanySuggestionType } from 'constants/BrokerCompanySuggestionType';
import { RentTypes } from 'constants/rentTypes';

import { IProperty } from 'interfaces/IProperty';
import { IPropertyCompany } from 'interfaces/IPropertyCompany';
import { IdName } from 'interfaces/IdName';
import { SEARCH_COMPANIES_PROPERTIES_QUERY } from 'graphql/common';
import { BUILD_OUT_TYPES_QUERY } from 'graphql/buildOut';
import {
  getFieldNameForLease,
  getFieldValueForLease,
} from 'utils/unitsOfMeasurement';
import { isMonthlyTimeMeasurement } from 'utils/leases';
import { currencyInputFormatter } from 'utils/formatters/currency';
import { isUkProperty } from 'utils/properties';
import { translateText } from 'utils/i18n';

import TIAllowance from '../../FormFields/TIAllowance/index';
import SelectProperty from '../../FormFields/SelectProperty';
import SizeFields from '../../FormFields/SizeFields';
import RentType from '../../FormFields/RentType';
import { calculateAdditionalConcessions } from '../utils';
import styles from '../../FormSection.module.scss';
import { LeaseFormSectionProps } from '..';
import EscalationFields from '../../FormFields/EscalationFields';
import { updateBooleanOptions } from '../../formUtils';
import { TaxAndOpEx } from '../../FormFields/TaxAndOpEx';
import { BrokerAndContact } from '../../FormFields/BrokerAndContact';

interface Props extends LeaseFormSectionProps {
  leaseCurrencySymbol: string;
  unitOfMeasurement: string;
  handleChangedSizeGrowth: () => void;
  handleTriggeredCalculation: () => void;
  leaseProperty?: IProperty;
}

export const LeaseTermsSection: React.FC<Props> = ({
  leaseCurrencySymbol,
  leaseInput,
  handleTriggeredCalculation,
  handleChangedSizeGrowth,
  unitOfMeasurement,

  updateLeaseProperty,
  leaseProperty,
}) => {
  const { t } = useTranslation();

  const { data: buildOutTypes } = useQuery<{ buildOutTypes: IdName[] }>(
    BUILD_OUT_TYPES_QUERY,
  );

  const measurementSystem =
    leaseInput?.measurementSystem || leaseInput?.property?.measurementSystem;

  const isPropertyUk = isUkProperty(leaseProperty);

  const updateAdditionalConcessions = (value: string) => {
    updateLeaseProperty('additionalConcessions', value);

    calculateAdditionalConcessions({
      additionalConcessions: value,
      leaseInput,
      measurementSystem,
      updateLeaseProperty,
    });
  };

  const updateAdditionalConcessionsPSF = (value: string) => {
    updateLeaseProperty(
      getFieldNameForLease(
        'additionalConcessionsPSF',
        leaseInput,
        measurementSystem,
      ),
      value,
    );

    calculateAdditionalConcessions({
      additionalConcessionsPSF: value,
      leaseInput,
      measurementSystem,
      updateLeaseProperty,
    });
  };

  return (
    <>
      <EscalationFields
        formType="lease"
        isPropertyUk={isPropertyUk}
        escalation={leaseInput?.escalation ?? ''}
        escalationIncrement={leaseInput?.escalationIncrement ?? ''}
        escalationIncrementType={leaseInput?.escalationIncrementType}
        escalationType={leaseInput?.escalationType ?? ''}
        escalationCap={leaseInput?.escalationCap}
        escalationCollar={leaseInput?.escalationCollar}
        currencySymbol={leaseCurrencySymbol}
        onUpdateHandler={updateLeaseProperty}
        upwardOnly={leaseInput?.upwardOnly}
        multipleBasis={leaseInput?.multipleBasis}
      />
      <FreeRentMonths
        property={leaseProperty}
        onChange={updateLeaseProperty}
        value={leaseInput.freeRentMonths}
      />
      {!isPropertyUk && (
        <RentType
          property={leaseProperty}
          rentType={RentTypes.FREE_RENT_TYPE}
          onChange={updateLeaseProperty}
          selectedItem={leaseInput?.freeRentType}
        />
      )}
      <TIAllowance
        property={leaseProperty}
        onChange={updateLeaseProperty}
        currencySymbol={leaseCurrencySymbol}
        unitOfMeasurement={unitOfMeasurement}
        value={leaseInput.tenantImprovementAllowance}
      />
      <FormControl
        label={translateText(
          `${I18N_AVANT_PLATFORM_LEASE_LABEL_PATH}.additionalConcessions`,
        )}
        wrapperClassName={styles['form-row']}
      >
        <CurrencyInput
          currencySymbol={leaseCurrencySymbol}
          iconPosition={'left'}
          id="additional-concessions"
          onChange={updateAdditionalConcessions}
          formatter={currencyInputFormatter}
          placeholder={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.enter`)}
          value={leaseInput.additionalConcessions}
        />
      </FormControl>
      <FormControl
        label={translateText(
          `${I18N_AVANT_PLATFORM_LEASE_LABEL_PATH}.additionalConcessionsPSF`,
          { unit: UNITS_MEASUREMENT_AREA[unitOfMeasurement] },
        )}
        wrapperClassName={styles['form-row']}
      >
        <div className={styles['large-input-container']}>
          <CurrencyInput
            currencySymbol={leaseCurrencySymbol}
            iconPosition={'left'}
            id="additional-concessions-psf"
            onChange={updateAdditionalConcessionsPSF}
            formatter={currencyInputFormatter}
            placeholder={translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.enter`,
            )}
            value={getFieldValueForLease(
              'additionalConcessionsPSF',
              leaseInput,
              measurementSystem,
            )}
          />
          <UnitOfMeasurementLegend unitOfMeasurement={unitOfMeasurement} />
        </div>
      </FormControl>
      <TaxAndOpEx
        date={leaseInput?.commencementDate || undefined}
        unitOfMeasurement={unitOfMeasurement}
        onChange={updateLeaseProperty}
        property={leaseProperty}
        value={{
          tax: leaseInput.tax,
          taxAndOpex: leaseInput.taxAndOpex,
          opex: leaseInput.opex,
        }}
      />
      <NetEffectiveRentField
        updateLeaseProperty={updateLeaseProperty}
        leaseCurrencySymbol={leaseCurrencySymbol}
        requiredFields={{
          NetEffectiveRentCustom: leaseInput.netEffectiveRentCustom,
          netEffectiveRent: leaseInput.netEffectiveRent,
          netEffectiveRentMonthly: leaseInput.netEffectiveRentMonthly,
          isMonthly: isMonthlyTimeMeasurement(leaseInput.timeMeasurement),
        }}
      />
      <NetEffectiveRentField
        updateLeaseProperty={updateLeaseProperty}
        leaseCurrencySymbol={leaseCurrencySymbol}
        isCustom={true}
        requiredFields={{
          netEffectiveRentCustom: leaseInput.netEffectiveRentCustom,
          netEffectiveRent: leaseInput.netEffectiveRent,
          netEffectiveRentMonthly: leaseInput.netEffectiveRentMonthly,
          isMonthly: isMonthlyTimeMeasurement(leaseInput.timeMeasurement),
        }}
      />
      <FormControl
        label={t(`${I18N_PLATFORM_COMMON_WORD_PATH}.buildOut`)}
        wrapperClassName={styles['form-row']}
      >
        <ChoiceInput
          selectedItem={{
            id: leaseInput.buildOutType?.id,
          }}
          data={buildOutTypes?.buildOutTypes ?? []}
          containerWrapperClassName={styles['radio-input-container']}
          onChange={item => {
            updateLeaseProperty('buildOutType', item ? { id: item?.id } : null);
          }}
        />
      </FormControl>

      <FormControl
        label={t(`${I18N_PLATFORM_COMMON_WORD_PATH}.furnished`)}
        wrapperClassName={styles['form-row']}
      >
        <ChoiceInput
          labelFieldName="id"
          selectedItem={{ name: leaseInput.furnished }}
          data={YES_NO_OPTIONS}
          containerWrapperClassName={styles['radio-input-container']}
          onChange={v =>
            updateLeaseProperty('furnished', updateBooleanOptions(v))
          }
        />
      </FormControl>
      <BrokerAndContact
        id="tenant-broker-input"
        isMultiSelect={false}
        labels={{
          company: translateText(
            `${I18N_PLATFORM_ROLES_COMPANY_LABEL_PATH}.tenantRepBroker`,
          ),
          companyBtn: translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.createTenant`,
          ),
          companyPlaceholder: t(
            `${I18N_PLATFORM_ROLES_COMPANY_PROMPT_PATH}.enterBrokerName`,
          ),
          contact: translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.tenantRepBrokerContact`,
          ),
          contactPlaceholder: translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.enterTenantRepBrokerContact`,
          ),
        }}
        onChange={updateLeaseProperty}
        fieldName="tenantBrokers"
        type={BrokerCompanySuggestionType.LEASE}
        brokers={leaseInput?.['tenantBrokers'] ?? []}
        marketId={leaseProperty?.market?.id!}
        propertyTypeId={leaseProperty?.propertyType?.id}
      />

      <BrokerAndContact
        id="landlord-broker-input"
        isMultiSelect={false}
        labels={{
          company: translateText(
            `${I18N_PLATFORM_ROLES_COMPANY_LABEL_PATH}.landlordBroker`,
          ),
          companyBtn: translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.createTenant`,
          ),
          companyPlaceholder: translateText(
            `${I18N_PLATFORM_ROLES_COMPANY_PROMPT_PATH}.enterBrokerName`,
          ),
          contact: translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.landlordBrokerContact`,
          ),
          contactPlaceholder: translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.enterLandlordBrokerContact`,
          ),
        }}
        fieldName="landlordBrokers"
        type={BrokerCompanySuggestionType.LEASE}
        brokers={leaseInput?.['landlordBrokers'] ?? []}
        marketId={leaseProperty?.market?.id!}
        propertyTypeId={leaseProperty?.propertyType?.id}
        onChange={updateLeaseProperty}
      />

      <SelectProperty
        id="leases-property-prioraddress-id"
        initialValue={leaseInput?.priorAddress?.primaryAddress}
        label={translateText(
          `${I18N_AVANT_PLATFORM_LEASE_LABEL_PATH}.priorAddress`,
        )}
        value={{
          id: leaseInput.priorAddress?.id,
          primaryAddress: leaseInput.priorAddress?.primaryAddress,
          name: leaseInput.priorAddress?.name,
        }}
        graphqlParams={{
          query: SEARCH_COMPANIES_PROPERTIES_QUERY,
          field: 'name',
          resultDataName: 'propertiesCompanies',
          onlyProperties: true,
        }}
        parseResults={(data: IPropertyCompany[]): IPropertyCompany[] => {
          return data.map(propertyCompany => ({
            ...propertyCompany,
            name: propertyCompany.displayAddress || propertyCompany.name,
          }));
        }}
        onChange={value => {
          value.id = value.propertyId ?? value.id;
          value.primaryAddress = value.primaryAddress ?? value.name;
          updateLeaseProperty('priorAddress', value);
        }}
        onClear={() => {
          updateLeaseProperty('priorAddress', null);
        }}
      />

      <CompanySearchCombo
        label={translateText(
          `${I18N_AVANT_PLATFORM_LEASE_LABEL_PATH}.ownerAtTimeOfLease`,
        )}
        btnLabel={translateText(
          `${I18N_PLATFORM_ROLES_COMPANY_ACTION_PATH}.createOwner`,
        )}
        placeholder={translateText(
          `${I18N_PLATFORM_ROLES_COMPANY_PROMPT_PATH}.enterOwnerNames`,
        )}
        wrapperClassName={styles['form-row']}
        inputId="lease-owner-at-time-of-lease"
        companies={leaseInput?.owners ?? []}
        isMultiple
        onCompanySelection={(value: IdName) => {
          const newValue = value?.custom || value;
          newValue?.id &&
            updateLeaseProperty('owners', [
              ...(leaseInput.owners || []),
              newValue,
            ]);
        }}
        onRemoveItem={(owner: IdName) => {
          updateLeaseProperty(
            'owners',
            (leaseInput.owners || []).filter(
              (value: IdName) => value.id !== owner.id,
            ),
          );
        }}
      />

      <SizeFields
        id="lease-prior-size"
        field="priorSize"
        input={leaseInput}
        labelName={translateText(
          `${I18N_AVANT_PLATFORM_LEASE_LABEL_PATH}.priorSize`,
        )}
        property={leaseProperty}
        onChange={(field, value) => {
          updateLeaseProperty(field, value);
          handleTriggeredCalculation();
        }}
        unitOfMeasurement={unitOfMeasurement}
        disableCalculationSizes={true}
      />
      <FormControl
        label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.sizeGrowth`)}
        wrapperClassName={styles['form-row']}
      >
        <ChoiceInput
          id="lease-size-growth"
          data-testid="lease-size-growth"
          data={SIZE_GROWTH_OPTIONS}
          itemWrapperClassName={styles['form-choice-input']}
          selectedItem={(leaseInput.expandingContracting as IdName) ?? null}
          labelFieldName="name"
          onChange={item => {
            updateLeaseProperty('expandingContracting', item);
            handleChangedSizeGrowth();
          }}
        />
      </FormControl>
      <FormControl
        label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.note`)}
        wrapperClassName={styles['form-row']}
        isMultiline
      >
        <div className={styles['large-input-container']}>
          <textarea
            id="lease-comment"
            data-testid="lease-comment"
            placeholder={translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.enterNote`,
            )}
            className={styles['comment-area']}
            onChange={e => updateLeaseProperty('comments', e.target.value)}
            value={leaseInput.comments || ''}
          />
        </div>
      </FormControl>
    </>
  );
};
